import { SITE_NAME, SITE_TAGLINE, SITE_TYPE } from '@/config/site';

/**
 * Every image slot a Rumio site has, with the size and format it expects.
 *
 * ## Why this exists
 *
 * Before this file, the dimensions a designer needed were `targetWidth` /
 * `targetHeight` props buried in SectionConfigEditor.tsx. The one file that
 * looked like a spec — `data/placeholder-images.ts` — pointed at thirteen JPEGs
 * that do not exist, and only its `aiPrompt` field was ever read; the width,
 * height and usage strings were documentation nobody rendered. So "what size
 * should this be?" was answered by reading React source, or by asking, or by
 * guessing and finding out after upload.
 *
 * This is the answer to that question in one place: the editors read their crop
 * targets from here, and `/admin/design/checklist` renders the whole list with
 * what is currently filled.
 *
 * ## The prompts are templates, not copy
 *
 * `placeholder-images.ts` hardcoded seed-company prompts ("terracotta pots,
 * scattered seed packets") for every tenant — on a lodging or museum site the
 * AI Generate button suggested a greenhouse. Prompts here are built from the
 * site's own name, tagline and type at call time instead.
 *
 * ## Adding a slot
 *
 * Add it here, and it appears in the checklist and drives its editor's crop
 * ratio automatically. Do not reintroduce a hardcoded dimension in a component.
 */

export type AssetSurface =
  | 'brand'
  | 'homepage'
  | 'content'
  | 'catalog'
  | 'email'
  | 'social';

export interface AssetSpec {
  /** Stable key. For homepage sections this is `<section_type>.<field>`. */
  key: string;
  label: string;
  surface: AssetSurface;
  /** Where a visitor sees it, in plain language. */
  usage: string;
  width: number;
  height: number;
  /** What the pipeline stores. Uploads are re-encoded, so this is the output. */
  format: 'webp' | 'png' | 'svg' | 'ico';
  /**
   * True when the site looks broken or unfinished without it. Drives the
   * "missing" count on the checklist.
   */
  required?: boolean;
  /** Anything a designer would otherwise have to discover the hard way. */
  notes?: string;
  /** Seed for the AI Generate button. Receives the site's own brand context. */
  prompt?: (ctx: BrandContext) => string;
}

export interface BrandContext {
  siteName: string;
  tagline: string;
  siteType: string;
}

export function brandContext(): BrandContext {
  return { siteName: SITE_NAME, tagline: SITE_TAGLINE, siteType: SITE_TYPE };
}

/** Shared photographic direction, so generated imagery looks like one set. */
function editorial(subject: string, ctx: BrandContext): string {
  const brand = ctx.tagline ? `${ctx.siteName} — ${ctx.tagline}` : ctx.siteName;
  return (
    `${subject} for ${brand}. Editorial lifestyle photography, natural light, ` +
    `shallow depth of field, warm neutral palette, generous negative space for ` +
    `overlaid text, no words or logos in the image.`
  );
}

export const ASSET_SPECS: readonly AssetSpec[] = [
  // ---------------------------------------------------------------- brand --
  {
    key: 'store_logo_url',
    label: 'Primary logo',
    surface: 'brand',
    usage: 'Site header, admin sidebar, and the fallback social card',
    width: 512,
    height: 512,
    format: 'png',
    required: true,
    notes: 'Square. Transparent background — it sits on both light and dark surfaces.',
  },
  {
    key: 'store_logo_dark_url',
    label: 'Logo, dark backgrounds',
    surface: 'brand',
    usage: 'Header in dark mode, and the email header plate',
    width: 1280,
    height: 320,
    format: 'png',
    notes:
      'Horizontal lockup. No admin upload field exists for this yet — it is written by Brand Kit apply, so today it comes from a kit or not at all.',
  },
  {
    key: 'favicon_url',
    label: 'Favicon',
    surface: 'brand',
    usage: 'Browser tab and bookmark icon',
    width: 32,
    height: 32,
    format: 'png',
    required: true,
    notes:
      'Must read at 16px. Every tenant currently ships an auto-generated letter tile because no sites/<id>/assets/ directory exists — see the build warning.',
  },
  {
    key: 'apple_touch_icon_url',
    label: 'iOS home-screen icon',
    surface: 'brand',
    usage: 'Shown when someone adds the site to their iPhone home screen',
    width: 180,
    height: 180,
    format: 'png',
    notes:
      'Safari ignores the web manifest and will not use an SVG, so this PNG is ' +
      'the only iOS icon source. Design it with no transparency — iOS masks the ' +
      'corners itself and shows black edges through a transparent background. ' +
      'Applying a brand kit fills this automatically.',
  },
  {
    key: 'default_og_image',
    label: 'Social share card',
    surface: 'social',
    usage: 'The preview image when a link is shared to Slack, iMessage, X, Facebook',
    width: 1200,
    height: 630,
    format: 'png',
    required: true,
    notes:
      'The one asset most likely to be wrong: with none set, the 512² PWA icon is used, which crops badly at 1.91:1. Keep text inside the middle 80%.',
    prompt: (ctx) => editorial('A wide, uncluttered brand scene suitable as a social share card', ctx),
  },
  {
    key: 'host_photo_url',
    label: 'Host photo',
    surface: 'brand',
    usage: 'Host introduction on lodging sites',
    width: 512,
    height: 512,
    format: 'webp',
    notes: 'Square portrait, face centred.',
  },

  // ------------------------------------------------------------- homepage --
  {
    key: 'hero.image_url',
    label: 'Hero background',
    surface: 'homepage',
    usage: 'Full-bleed background behind the homepage headline',
    width: 1920,
    height: 1080,
    format: 'webp',
    required: true,
    notes:
      'Text is overlaid, so keep the centre calm and avoid busy detail where the headline sits. This is the largest image on the site and the one that decides page-load feel.',
    prompt: (ctx) => editorial('A wide establishing hero photograph', ctx),
  },
  {
    key: 'story.image_url',
    label: 'Story image',
    surface: 'homepage',
    usage: 'The image half of the split "our story" section',
    width: 1200,
    height: 900,
    format: 'webp',
    prompt: (ctx) => editorial('An intimate, human detail shot that suggests craft and care', ctx),
  },
  {
    key: 'image_banner.image_url',
    label: 'Full-width banner',
    surface: 'homepage',
    usage: 'Wide mid-page banner',
    width: 2100,
    height: 900,
    format: 'webp',
    notes: 'Very wide (21:9). Anything near the top or bottom edge will be cropped on mobile.',
    prompt: (ctx) => editorial('A wide panoramic scene with space across the middle for a caption', ctx),
  },
  {
    key: 'seasonal_spotlight.image_url',
    label: 'Seasonal spotlight',
    surface: 'homepage',
    usage: 'The rotating seasonal / campaign feature',
    width: 1200,
    height: 900,
    format: 'webp',
    prompt: (ctx) => editorial('A seasonal feature photograph', ctx),
  },
  {
    key: 'image_gallery.images',
    label: 'Gallery images',
    surface: 'homepage',
    usage: 'Each tile in the homepage gallery grid',
    width: 1200,
    height: 900,
    format: 'webp',
    notes: 'Unlimited count. They read as a set, so keep colour and light consistent across them.',
  },
  {
    key: 'course_highlight.image_url',
    label: 'Course highlight',
    surface: 'homepage',
    usage: 'Featured course block',
    width: 1200,
    height: 800,
    format: 'webp',
  },
  {
    key: 'instructor_bios.image_url',
    label: 'Instructor photo',
    surface: 'homepage',
    usage: 'Each instructor in the bios row',
    width: 400,
    height: 400,
    format: 'webp',
    notes: 'Square, cropped to a circle at render time — keep the face centred and away from edges.',
  },
  {
    key: 'trust_badges.image_url',
    label: 'Trust badge',
    surface: 'homepage',
    usage: 'Optional image instead of an icon on a trust badge',
    width: 200,
    height: 200,
    format: 'png',
    notes: 'Transparent background.',
  },
  {
    key: 'video_showcase.thumbnail_url',
    label: 'Video thumbnail',
    surface: 'homepage',
    usage: 'Poster frame before a showcase video plays',
    width: 1280,
    height: 720,
    format: 'webp',
  },

  // -- Vertical sections. These render images but have no editor yet: they
  // -- fall through to a raw-JSON textarea, so the sizes were undiscoverable.
  {
    key: 'current_exhibit_hero.hero_image',
    label: 'Exhibit hero (museum)',
    surface: 'homepage',
    usage: 'Current-exhibit banner on museum sites',
    width: 1920,
    height: 1080,
    format: 'webp',
    notes: 'Edited as raw JSON today — no upload field yet.',
  },
  {
    key: 'mission_brief.image',
    label: 'Mission image (museum)',
    surface: 'homepage',
    usage: 'Mission statement block on museum sites',
    width: 1200,
    height: 900,
    format: 'webp',
    notes: 'Edited as raw JSON today — no upload field yet.',
  },
  {
    key: 'availability_search.hero_image',
    label: 'Booking hero (lodging)',
    surface: 'homepage',
    usage: 'Background behind the availability search on lodging sites',
    width: 1920,
    height: 1080,
    format: 'webp',
    notes: 'Edited as raw JSON today — no upload field yet.',
  },
  {
    key: 'host_intro.image_url',
    label: 'Host intro image (lodging)',
    surface: 'homepage',
    usage: 'Host introduction block; falls back to the host photo setting',
    width: 1200,
    height: 900,
    format: 'webp',
    notes: 'Edited as raw JSON today — no upload field yet.',
  },
  {
    key: 'area_highlights.image_url',
    label: 'Area highlight (lodging)',
    surface: 'homepage',
    usage: 'Local-area feature block',
    width: 1200,
    height: 900,
    format: 'webp',
    notes: 'Edited as raw JSON today — no upload field yet.',
  },
  {
    key: 'play_now_hero.hero_image',
    label: 'Play-now hero (social casino)',
    surface: 'homepage',
    usage: 'Homepage hero on social-casino sites',
    width: 1920,
    height: 1080,
    format: 'webp',
    notes:
      'Rendered under a fixed dark scrim, so a light image will read much darker than the file. Edited as raw JSON today.',
  },

  // -------------------------------------------------------------- content --
  {
    key: 'blog.featured_image_url',
    label: 'Blog featured image',
    surface: 'content',
    usage: 'Post header, blog index cards, and the post’s social card',
    width: 1200,
    height: 630,
    format: 'webp',
    notes: 'Doubles as the share card for that post, so the same 1.91:1 safe-area rule applies.',
  },
  {
    key: 'category.image_url',
    label: 'Category image',
    surface: 'catalog',
    usage: 'Category cards in the shop navigation',
    width: 900,
    height: 1200,
    format: 'webp',
    notes: 'Portrait 3:4. The admin field is a plain URL box today — no uploader, no crop.',
  },
  {
    key: 'product.images',
    label: 'Product images',
    surface: 'catalog',
    usage: 'Product grid and detail gallery',
    width: 1200,
    height: 1600,
    format: 'webp',
    notes:
      'Stored at 1200w plus a 400w thumbnail — the only slot on the platform with two sizes, and the only one with a responsive srcset. Grid tiles crop to 3:4, so keep the subject centred.',
  },
  {
    key: 'bundle.image_url',
    label: 'Bundle image',
    surface: 'catalog',
    usage: 'Product-bundle offer card',
    width: 1200,
    height: 675,
    format: 'webp',
  },
  {
    key: 'property.images',
    label: 'Property photos',
    surface: 'catalog',
    usage: 'Listing gallery on lodging sites',
    width: 1600,
    height: 1067,
    format: 'webp',
    notes: 'Alt text defaults to the filename, so name the files meaningfully.',
  },

  // ---------------------------------------------------------------- email --
  {
    key: 'email.logoUrl',
    label: 'Email header logo',
    surface: 'email',
    usage: 'Top of every transactional and campaign email',
    width: 600,
    height: 200,
    format: 'png',
    notes:
      'Rendered at max-height 48px, so supply it at 3–4× for retina. Must read on the email header colour, which is set per site.',
  },
  {
    key: 'newsletter.hero',
    label: 'Newsletter hero',
    surface: 'email',
    usage: 'Lead image in a campaign email',
    width: 1200,
    height: 630,
    format: 'webp',
    notes: 'Rendered at 600px wide in email clients. Avoid fine detail and small text.',
  },
];

/** Fast lookup by key. */
export const ASSET_SPEC_BY_KEY: Readonly<Record<string, AssetSpec>> = Object.fromEntries(
  ASSET_SPECS.map((s) => [s.key, s]),
);

/**
 * Crop target for a slot, for `ImageUploader`'s aspect lock and its
 * "Recommended: W × H" hint. Returns undefined for an unknown key so a caller
 * degrades to a free crop rather than throwing.
 */
export function assetTarget(key: string): { width: number; height: number } | undefined {
  const spec = ASSET_SPEC_BY_KEY[key];
  return spec ? { width: spec.width, height: spec.height } : undefined;
}

/** Brand-aware AI prompt for a slot, if it has one. */
export function assetPrompt(key: string, ctx: BrandContext = brandContext()): string | undefined {
  return ASSET_SPEC_BY_KEY[key]?.prompt?.(ctx);
}

export const SURFACE_LABELS: Readonly<Record<AssetSurface, string>> = {
  brand: 'Brand',
  homepage: 'Homepage',
  content: 'Content',
  catalog: 'Catalog',
  email: 'Email',
  social: 'Social',
};
